import { useState, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Download, Loader2, AlertCircle, ExternalLink } from 'lucide-react';

// Worker setup — pinned CDN worker matching pdfjs-dist version shipped by react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ url, title = 'Document' }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(null);

  // Resolve full URL (backend) if relative
  const fullUrl = useMemo(() => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    return `${backend}${url}`;
  }, [url]);

  // File object for react-pdf — include credentials so authenticated routes work
  const file = useMemo(() => (
    fullUrl ? { url: fullUrl, withCredentials: false } : null
  ), [fullUrl]);

  const options = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@5.4.296/cmaps/',
    cMapPacked: true,
  }), []);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPageNumber(1);
    setLoadError(false);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.error('PDF load error:', err);
    setLoadError(true);
  }, []);

  const containerRef = useCallback((node) => {
    if (node) setContainerWidth(node.clientWidth);
  }, []);

  if (!fullUrl) return null;

  // Fallback UI if rendering failed (e.g., corrupt file or CORS)
  if (loadError) {
    return (
      <div
        className="w-full border rounded bg-gray-50 p-6 flex flex-col items-center justify-center text-center gap-3"
        data-testid="pdf-viewer-error"
      >
        <AlertCircle className="w-10 h-10 text-amber-500" />
        <p className="text-sm text-gray-700">
          This PDF couldn&apos;t be previewed in-app.
        </p>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="pdf-open-new-tab">
            <a href={fullUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in new tab
            </a>
          </Button>
          <Button asChild size="sm" data-testid="pdf-download">
            <a href={fullUrl} download>
              <Download className="w-4 h-4 mr-2" />
              Download
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full border rounded bg-gray-50 overflow-hidden"
      data-testid="pdf-viewer"
    >
      {/* Controls */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!numPages || pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            data-testid="pdf-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600 min-w-[80px] text-center" data-testid="pdf-page-indicator">
            {numPages ? `${pageNumber} / ${numPages}` : '—'}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={!numPages || pageNumber >= numPages}
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            data-testid="pdf-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="ghost" data-testid="pdf-open-new-tab-btn">
            <a href={fullUrl} target="_blank" rel="noopener noreferrer" title="Open in new tab">
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
          <Button asChild size="sm" variant="ghost" data-testid="pdf-download-btn">
            <a href={fullUrl} download title="Download">
              <Download className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </div>

      {/* Document */}
      <div className="overflow-auto max-h-[70vh] flex justify-center">
        <Document
          file={file}
          options={options}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading {title}…
            </div>
          }
          error={
            <div className="py-8 text-sm text-red-600">Failed to load document.</div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={containerWidth ? Math.min(containerWidth - 4, 900) : undefined}
            renderAnnotationLayer={true}
            renderTextLayer={true}
          />
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;
